/**
 * Intercom Messenger client wrapper.
 *
 * The Messenger launcher is shown to EVERYONE so support is never hidden
 * behind cookie consent. It boots in a "privacy-light" mode by default —
 * just the messenger, with NO user identification and NO tracking traits/
 * events. Once the user grants analytics consent (or explicitly initiates
 * live support) it upgrades in place to the full identified/tracked
 * experience; revoking consent downgrades it back to privacy-light without
 * shutting the launcher down. Intercom is still listed under the analytics
 * vendor list (see ConsentBanner) because identification/tracking is what's
 * gated, not the messenger itself.
 * Identity verification (HMAC) is fetched from the server at /users/me/intercom-hash.
 *
 * Public API:
 *   - configureIntercom({ appId })   — set the workspace id (called from PlatformProvider)
 *   - bootIntercom(traits)           — boot anonymous or logged-in messenger
 *   - updateIntercom(traits)         — refresh traits (e.g. after locale change)
 *   - shutdownIntercom()             — clear messenger session (on sign-out)
 *   - showIntercom() / hideIntercom()
 *   - showNewSupportMessage(prefill) — open Messenger composer with prefilled text
 *   - trackIntercomEvent(name, meta) — fire a metadata-tagged event for Fin context
 *   - openSupportChat(opts)          — show widget with prefill+context, mailto fallback
 *   - isIntercomConfigured() / isIntercomReady()
 */
import { getConsent } from "./analytics/consent";

interface IntercomSettings {
  app_id: string;
  user_id?: string;
  user_hash?: string;
  /**
   * Messenger Security with JWTs (replaces the legacy HMAC user_hash).
   * When present, Intercom verifies the signature with our shared
   * Messenger API Secret and trusts the embedded user_id / email,
   * which then flows through to Fin Custom Actions for ownership
   * checks. Issued at the same time as user_hash; Intercom honours
   * either one, so we send both during the migration window.
   */
  intercom_user_jwt?: string;
  email?: string;
  name?: string;
  created_at?: number;
  language_override?: string;
  hide_default_launcher?: boolean;
  bottom_padding?: number;
  custom_launcher_selector?: string;
  // BAC custom traits
  role?: string;
  currency?: string;
  signup_date?: string;
}

declare global {
  interface Window {
    intercomSettings?: IntercomSettings;
    Intercom?: ((command: string, ...args: unknown[]) => void) & {
      booted?: boolean;
    };
  }
}

let cachedAppId: string | null = null;
let scriptReady = false;
let scriptReadyPromise: Promise<boolean> | null = null;
let lastBootTraits: BootTraits | null = null;
let launcherHiddenOverride = false;
let visiblePublicMapCount = 0;
const DEFAULT_LAUNCHER_BOTTOM_PADDING = 20;
const MAP_LAUNCHER_BOTTOM_PADDING = 104;
// Tracks whether the CURRENT live Intercom session was booted with the user's
// verified identity (full mode) vs anonymously (privacy-light). Switching
// between the two requires a clean shutdown + re-boot so Intercom never merges
// an anonymous lead with the verified user, nor leaks identity after a revoke.
let bootedIdentified = false;
let messengerVisible = false;
let observedMessenger: Window["Intercom"];
let visibilityWaiter: ((visible: boolean) => void) | null = null;

/**
 * Durable storage key for the functional support-consent override. This is
 * deliberately SEPARATE from the analytics/marketing consent blob
 * (`bac_consent_v1`) — it records only that the user explicitly initiated
 * live support, which loads the Messenger regardless of the analytics-cookie
 * choice. It must survive the full-page reload that `signOut` performs (so a
 * user who engaged with support keeps their chat + history after logout/login)
 * and is treated as browser-level state, so it is NOT cleared by the auth
 * purge (`clearAllAuthStorage` / `clearUserScopedAppCaches`).
 */
const SUPPORT_CONSENT_KEY = "bac_support_consent_v1";

function readPersistedSupportConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(SUPPORT_CONSENT_KEY) === "1";
  } catch {
    return false;
  }
}

// Initialise from durable storage at module load so the very first
// bootIntercom() on a cold page (after a reload or re-login) already sees a
// prior support engagement, even with analytics cookies declined.
let explicitSupportConsent = readPersistedSupportConsent();

export interface BootTraits {
  userId?: string | number;
  email?: string;
  name?: string;
  userHash?: string;
  /** Signed JWT issued by /users/me/intercom-hash for Messenger Security with JWTs. */
  userJwt?: string;
  role?: string;
  locale?: string;
  currency?: string;
  signupDate?: string;
}

export function configureIntercom(opts: { appId: string | null }): void {
  if (!opts.appId) return;
  const isNew = cachedAppId !== opts.appId;
  cachedAppId = opts.appId;
  // If traits were queued before the app id arrived (race between auth and
  // platform config fetch), boot now so logged-in users always get the
  // verified-identity Messenger.
  if (isNew && lastBootTraits) {
    void bootIntercom(lastBootTraits);
  }
}

export function isIntercomConfigured(): boolean {
  return !!cachedAppId;
}

// De-duplicated on-demand fetch of the Intercom workspace id. Boot-time
// configuration is deferred to idle (task #7081 — INP), so a support CTA
// clicked before the idle callback runs must be able to configure the
// Messenger itself; this is that path. Best-effort: resolves false when the
// platform config is unavailable, letting callers fall back to mailto.
let configFetchPromise: Promise<boolean> | null = null;
const CONFIG_TIMEOUT_MS = 5000;

export async function ensureIntercomConfigured(): Promise<boolean> {
  if (cachedAppId) return true;
  if (typeof window === "undefined") return false;
  if (!configFetchPromise) {
    const controller = new AbortController();
    let expired = false;
    let timer: ReturnType<typeof setTimeout>;
    const request = Promise.resolve().then(async () => {
      const response = await fetch(`${import.meta.env.BASE_URL || "/"}api/config/platform`, {
        signal: controller.signal,
      });
      const cfg = response.ok ? await response.json() : null;
      const appId = cfg?.intercom?.appId;
      if (expired || typeof appId !== "string" || !appId.trim()) return false;
      configureIntercom({ appId });
      return true;
    }).catch(() => false);
    const deadline = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => {
        expired = true;
        controller.abort();
        resolve(false);
      }, CONFIG_TIMEOUT_MS);
    });
    configFetchPromise = Promise.race([request, deadline]).finally(() => {
      clearTimeout(timer);
      configFetchPromise = null;
    });
  }
  return configFetchPromise;
}

/**
 * Whether Intercom may attach the user's identity + traits and fire tracking
 * events. This does NOT gate whether the Messenger loads — the launcher is
 * shown to everyone (privacy-light). It only gates identification/tracking:
 * granted by analytics consent, OR by an explicit, user-initiated support
 * request (grantSupportConsent) which is a functional interaction — not
 * background tracking — and which also covers environments that never surface
 * a consent banner (the embedded mobile WebView).
 */
function trackingAllowed(): boolean {
  return (
    explicitSupportConsent ||
    readPersistedSupportConsent() ||
    getConsent().analytics === true
  );
}

/** Does the given trait bag carry enough to identify a logged-in user? */
function hasIdentity(traits: BootTraits | null | undefined): boolean {
  return !!(
    traits &&
    (traits.userId != null || !!traits.userHash || !!traits.userJwt)
  );
}

/**
 * Record that the user explicitly asked for live support (e.g. tapped a
 * "Chat with Shelby" / contact CTA). An explicit support request is a
 * functional interaction the user initiated, so it is treated as consent
 * to load the Messenger even when analytics cookies were declined or the
 * environment never surfaces a consent banner (the embedded mobile
 * WebView). Without this, the Messenger stays gated behind analytics
 * consent and every support tap silently falls back to email.
 */
export function grantSupportConsent(): void {
  explicitSupportConsent = true;
  // Persist so the override survives the full-page reload that signOut
  // performs (which resets this module's state) and the subsequent
  // re-login, keeping the Messenger + conversation history available even
  // with analytics cookies declined.
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(SUPPORT_CONSENT_KEY, "1");
    } catch {
      /* storage may be unavailable (private mode / quota) — in-memory
         flag still loads the Messenger for the current page lifetime. */
    }
  }
}

type IntercomFn = ((command: string, ...args: unknown[]) => void) & {
  booted?: boolean;
  q?: unknown[][];
  c?: (args: unknown[]) => void;
};

function injectScript(appId: string): Promise<boolean> {
  if (scriptReadyPromise) return scriptReadyPromise;
  scriptReadyPromise = new Promise<boolean>((resolve) => {
    const existing = window.Intercom as IntercomFn | undefined;
    if (typeof existing === "function" && existing.booted) {
      scriptReady = true;
      resolve(true);
      return;
    }
    // Standard Intercom snippet, condensed. The stub queues calls until the
    // real widget script loads and replaces window.Intercom.
    const stub = function (this: unknown, ...args: unknown[]): void {
      stub.c?.(args);
    } as IntercomFn;
    const queue: unknown[][] = [];
    stub.q = queue;
    stub.c = (args: unknown[]) => {
      queue.push(args);
    };
    window.Intercom = stub;
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = `https://widget.intercom.io/widget/${appId}`;
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      s.onload = null;
      s.onerror = null;
      scriptReady = ok;
      if (!ok) {
        scriptReadyPromise = null;
        s.remove();
        if (window.Intercom === stub) delete window.Intercom;
      }
      resolve(ok);
    };
    s.onload = () => {
      // A load event alone is not proof that the SDK replaced its queue stub.
      finish(typeof window.Intercom === "function" && window.Intercom !== stub);
    };
    s.onerror = () => finish(false);
    // Failsafe: if neither onload nor onerror fires within 5s (e.g. ad
    // blocker silently drops the request), treat the widget as unavailable.
    const timer = setTimeout(() => finish(false), 5000);
    try {
      document.head.appendChild(s);
    } catch {
      finish(false);
    }
  });
  // A synchronous append failure can occur before the promise is assigned.
  void scriptReadyPromise.then((ok) => { if (!ok) scriptReadyPromise = null; });
  return scriptReadyPromise;
}

export function isIntercomReady(): boolean {
  return scriptReady;
}

/**
 * Mobile-bug fix #2: hide the floating Messenger launcher on small viewports.
 * The default launcher (z-index 2147483647) sits over our sticky bottom CTA
 * (Book / Pay / Send) on phones, blocking the primary action. We still boot
 * Intercom so server-side conversations, programmatic `Intercom("show")`
 * calls (e.g. "Need help?" links inside support pages), and unread badges
 * keep working — only the floating bubble is hidden. The threshold matches
 * Tailwind's `md` breakpoint so tablets still get the launcher.
 */
function shouldHideDefaultLauncher(): boolean {
  if (launcherHiddenOverride) return true;
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768;
}

function readBottomSafeAreaInset(): number {
  if (typeof window === "undefined" || typeof document === "undefined") return 0;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom,0px)";
  document.body.appendChild(probe);
  const inset = Number.parseFloat(window.getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return inset;
}

function mapAwareBottomPadding(): number | undefined {
  if (typeof window === "undefined" || window.innerWidth < 768 || visiblePublicMapCount === 0) {
    return undefined;
  }
  return MAP_LAUNCHER_BOTTOM_PADDING + readBottomSafeAreaInset();
}

function buildSettings(appId: string, traits: BootTraits | null): IntercomSettings {
  const s: IntercomSettings = { app_id: appId };
  if (shouldHideDefaultLauncher()) s.hide_default_launcher = true;
  const bottomPadding = mapAwareBottomPadding();
  if (bottomPadding !== undefined) s.bottom_padding = bottomPadding;
  if (!traits) return s;
  if (traits.userId != null) s.user_id = String(traits.userId);
  if (traits.email) s.email = traits.email;
  if (traits.name) s.name = traits.name;
  // Prefer JWT (Messenger Security with JWTs) when available; fall back to
  // legacy HMAC user_hash. Sending BOTH credentials simultaneously can be
  // rejected by Intercom in strict-mode workspaces, which manifests as the
  // launcher booting then immediately hiding for identified users.
  if (traits.userJwt) {
    s.intercom_user_jwt = traits.userJwt;
  } else if (traits.userHash) {
    s.user_hash = traits.userHash;
  }
  if (traits.locale) s.language_override = traits.locale.slice(0, 2);
  if (traits.role) s.role = traits.role;
  if (traits.currency) s.currency = traits.currency;
  if (traits.signupDate) {
    s.signup_date = traits.signupDate;
    const ts = new Date(traits.signupDate).getTime();
    if (!Number.isNaN(ts)) s.created_at = Math.floor(ts / 1000);
  }
  return s;
}

/**
 * SEO guard — never boot the Messenger for bots/prerender renders. Intercom
 * overwrites document.title with notification text ("Shelby says…") when a
 * proactive/unread message arrives; if Googlebot's renderer sees that, the
 * indexed title is destroyed. Bots also have no use for a support widget.
 */
function isBotOrPrerender(): boolean {
  if (typeof navigator === "undefined") return true;
  if (navigator.webdriver) return true;
  return /bot|crawl|spider|slurp|bingpreview|headless|prerender|lighthouse|google page speed|gtmetrix/i.test(
    navigator.userAgent || "",
  );
}

export async function bootIntercom(traits?: BootTraits | null): Promise<boolean> {
  try {
    return await performIntercomBoot(traits);
  } catch {
    return false;
  }
}

async function performIntercomBoot(traits?: BootTraits | null): Promise<boolean> {
  if (isBotOrPrerender()) return false;
  // Always remember the most recent traits so that a later configureIntercom
  // (when /config/platform resolves after auth) or consent grant can replay
  // the identified boot. Without this, identified boots silently downgrade
  // to anonymous when the platform-config fetch loses the race against auth.
  if (traits !== undefined) lastBootTraits = traits;
  const appId = cachedAppId;
  if (!appId) return false;

  // Reuse cached identified traits when the caller passes none (e.g. the
  // consent-change subscriber) so an identified user isn't downgraded to an
  // anonymous boot purely because no traits were re-supplied.
  const effectiveTraits = traits === undefined ? lastBootTraits : traits;

  // Privacy-light by default: the launcher is shown to EVERYONE regardless of
  // cookie choice. We only attach identity + traits (and, separately, fire
  // tracking events) once tracking is allowed — analytics cookies accepted, or
  // an explicit support request. Until then Intercom runs anonymously: just
  // the messenger, no identification, no traits. We still cache identified
  // traits above so a later consent grant can upgrade in place.
  const identified = trackingAllowed() && hasIdentity(effectiveTraits);
  const settings = buildSettings(appId, identified ? effectiveTraits || null : null);
  window.intercomSettings = settings;

  const ok = await injectScript(appId);
  if (!ok) return false;
  if (typeof window.Intercom !== "function") return false;

  if (window.Intercom.booted) {
    // A change between anonymous (privacy-light) and identified sessions needs
    // a clean shutdown + fresh boot so Intercom doesn't merge an anonymous
    // lead with the verified user (upgrade) or keep identity after a consent
    // revoke (downgrade). A same-mode refresh is just a cheap `update`.
    if (bootedIdentified !== identified) {
      window.Intercom("shutdown");
      messengerVisible = false;
      window.Intercom("boot", settings);
    } else {
      window.Intercom("update", settings);
    }
  } else {
    window.Intercom("boot", settings);
  }
  window.Intercom.booted = true;
  bootedIdentified = identified;
  return true;
}

export function updateIntercom(traits?: BootTraits | null): void {
  if (typeof window === "undefined" || typeof window.Intercom !== "function") return;
  const appId = cachedAppId;
  if (!appId) return;
  // Merge incoming traits over the last identified boot so identity tokens
  // (userHash / userJwt) carried by the original boot survive incremental
  // profile updates — callers (AuthProvider profile-sync useEffect) only
  // pass profile-derived fields, not identity material.
  const merged: BootTraits | null = traits
    ? { ...(lastBootTraits || {}), ...traits,
        userHash: traits.userHash ?? lastBootTraits?.userHash,
        userJwt: traits.userJwt ?? lastBootTraits?.userJwt }
    : lastBootTraits;
  // Persist the merged identity so subsequent boots/replays don't lose it.
  if (merged) lastBootTraits = merged;
  // Privacy-light guard: while tracking isn't allowed the live Messenger is
  // running anonymously, so do NOT push identity/traits into it here — that
  // would leak the user's identity before consent. The merged traits are
  // cached above and replayed by bootIntercom() the moment consent is granted.
  if (!trackingAllowed() || !bootedIdentified) return;
  const settings = buildSettings(appId, merged);
  window.intercomSettings = settings;
  window.Intercom("update", settings);
}

/**
 * Shut down the Intercom session. Pass `{ keepTraits: true }` for a
 * consent-driven shutdown (the user revoked analytics consent but is still
 * logged in) so a subsequent re-grant boots back into the identified
 * Messenger. Defaults to clearing traits — appropriate for explicit
 * sign-out / auth state reset.
 */
export function shutdownIntercom(opts?: { keepTraits?: boolean }): void {
  messengerVisible = false;
  if (!opts?.keepTraits) lastBootTraits = null;
  bootedIdentified = false;
  if (typeof window === "undefined" || typeof window.Intercom !== "function") return;
  window.Intercom("shutdown");
  if (window.Intercom) window.Intercom.booted = false;
}

/**
 * Show the Messenger if the real widget script has loaded. Returns false
 * (without queuing) when the script is not ready — callers should fall back
 * to mailto in that case.
 */
export function showIntercom(): boolean {
  if (typeof window === "undefined") return false;
  // The Messenger is loaded for everyone (privacy-light), so showing it does
  // not require tracking consent — only that the widget script is ready.
  if (!cachedAppId) return false;
  if (!scriptReady || typeof window.Intercom !== "function") return false;
  window.Intercom("show");
  return true;
}

/**
 * Open the Messenger's conversation inbox. Intercom's browser API does not
 * expose a safe "open this conversation id" command; once the requester is
 * identified, `showMessages` is the supported way to return them to the
 * thread Intercom owns. It is deliberately separate from `showIntercom()`,
 * which opens the generic Messenger home.
 */
export function showIntercomMessages(): boolean {
  if (typeof window === "undefined") return false;
  if (
    !bootedIdentified ||
    !cachedAppId ||
    !scriptReady ||
    typeof window.Intercom !== "function"
  ) {
    return false;
  }
  window.Intercom("showMessages");
  return true;
}

export function hideIntercom(): void {
  if (typeof window === "undefined" || typeof window.Intercom !== "function") return;
  window.Intercom("hide");
}

export function setLauncherVisibility(visible: boolean): void {
  launcherHiddenOverride = !visible;
  if (typeof window === "undefined" || typeof window.Intercom !== "function") return;
  window.Intercom("update", { hide_default_launcher: shouldHideDefaultLauncher() });
}

function syncLauncherPosition(): void {
  if (typeof window === "undefined" || typeof window.Intercom !== "function") return;
  window.Intercom("update", {
    hide_default_launcher: shouldHideDefaultLauncher(),
    bottom_padding: mapAwareBottomPadding() ?? DEFAULT_LAUNCHER_BOTTOM_PADDING,
  });
}

function handleMapViewportResize(): void {
  syncLauncherPosition();
}

/**
 * Reserve room above visible public maps for MapKit's bottom-right controls.
 * Returns an idempotent cleanup so concurrent maps cannot clear each other's
 * spacing when one becomes hidden or unmounts.
 */
export function acquirePublicMapLauncherSpacing(): () => void {
  visiblePublicMapCount += 1;
  if (visiblePublicMapCount === 1 && typeof window !== "undefined") {
    window.addEventListener("resize", handleMapViewportResize);
  }
  syncLauncherPosition();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    visiblePublicMapCount = Math.max(0, visiblePublicMapCount - 1);
    if (visiblePublicMapCount === 0 && typeof window !== "undefined") {
      window.removeEventListener("resize", handleMapViewportResize);
    }
    syncLauncherPosition();
  };
}

/** Open the Messenger composer with optional prefilled text. */
export function showNewSupportMessage(prefill?: string): boolean {
  if (typeof window === "undefined") return false;
  // User-initiated; the Messenger is loaded for everyone, so no tracking
  // consent gate here — only that the widget script is ready.
  if (!cachedAppId) return false;
  if (!scriptReady || typeof window.Intercom !== "function") return false;
  if (prefill && prefill.trim()) {
    window.Intercom("showNewMessage", prefill);
  } else {
    window.Intercom("show");
  }
  return true;
}

/** Fire a client-side Intercom event with metadata for Fin context. */
export function trackIntercomEvent(
  eventName: string,
  metadata?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  // Tracking events are gated: no analytics events fire before consent.
  if (!cachedAppId || !trackingAllowed()) return;
  if (typeof window.Intercom !== "function") return;
  // The stub will queue trackEvent calls until the real script loads, so we
  // don't need scriptReady here — Intercom replays the queue on init.
  if (metadata && Object.keys(metadata).length > 0) {
    window.Intercom("trackEvent", eventName, metadata);
  } else {
    window.Intercom("trackEvent", eventName);
  }
}

export interface OpenSupportChatOptions {
  /** Email destination for legacy navigation or an explicit UI fallback. */
  mailtoFallback: string;
  /** Optional pre-filled message text shown in the Messenger composer. */
  prefill?: string;
  /** Optional context metadata fired as `support_chat_opened` for Fin. */
  context?: Record<string, unknown>;
  /** Optional event name override (defaults to "support_chat_opened"). */
  eventName?: string;
  /** Optional subject appended to the mailto fallback URL. */
  subject?: string;
  /** UI callers render a visible fallback instead of navigating automatically. */
  visibleFallback?: boolean;
}

/**
 * Open the Messenger with optional prefill+context, falling back to a
 * mailto: when the widget is blocked or unavailable. Accepts a plain
 * mailto string for legacy callers.
 */
let supportOpenPromise: Promise<boolean> | null = null;

export function openSupportChat(
  optsOrMailto: OpenSupportChatOptions | string,
): Promise<boolean> {
  if (supportOpenPromise) return supportOpenPromise;
  const attempt = performSupportOpen(optsOrMailto);
  supportOpenPromise = attempt;
  void attempt.finally(() => { supportOpenPromise = null; });
  return attempt;
}

async function performSupportOpen(
  optsOrMailto: OpenSupportChatOptions | string,
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const opts: OpenSupportChatOptions =
    typeof optsOrMailto === "string"
      ? { mailtoFallback: optsOrMailto }
      : optsOrMailto;

  // An explicit tap on a support CTA is a user-initiated request for live
  // help, so load the Messenger even when analytics cookies were declined
  // (or no consent banner exists, as in the embedded mobile WebView).
  // mailto stays a true last resort below if the widget cannot load.
  try {
    grantSupportConsent();

    // Keep warm opens synchronous up to the show command. Cold opens have
    // bounded configuration and script waits; onShow confirms actual visibility.
    if (!isIntercomReady() || !window.Intercom?.booted) {
      if (!await ensureIntercomConfigured()) return unavailable();
      if (!await bootIntercom(lastBootTraits)) return unavailable();
    }
    if (!await confirmSupportVisible(opts.prefill)) return unavailable();
    trackIntercomEvent(opts.eventName || "support_chat_opened", {
      ...(opts.context || {}),
      page: window.location.pathname,
      prefill_present: !!(opts.prefill && opts.prefill.trim()),
    });
    return true;
  } catch {
    return unavailable();
  }

  function unavailable(): false {
    if (!opts.visibleFallback) {
      try { window.location.href = buildMailtoFallback(opts); } catch { /* caller still receives failure */ }
    }
    return false;
  }
}

function confirmSupportVisible(prefill?: string): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (visible: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (visibilityWaiter === finish) visibilityWaiter = null;
      resolve(visible);
    };
    const timer = setTimeout(() => finish(false), 3000);
    try {
      visibilityWaiter = finish;
      if (observedMessenger !== window.Intercom) {
        messengerVisible = false;
        window.Intercom?.("onShow", () => {
          messengerVisible = true;
          visibilityWaiter?.(true);
        });
        window.Intercom?.("onHide", () => { messengerVisible = false; });
        observedMessenger = window.Intercom;
      }
      if (!showNewSupportMessage(prefill)) finish(false);
      else if (messengerVisible) finish(true);
    } catch {
      finish(false);
    }
  });
}

/**
 * Open the requester-facing concierge thread list. A conversation id may be
 * included in a notification URL for correlation, but is never treated as an
 * authorization credential in the browser. Guests (or blocked Messenger
 * installs) retain the caller-provided email reply fallback.
 */
export async function openConciergeConversation(opts: {
  mailtoFallback: string;
  conversationId?: string | null;
}): Promise<boolean> {
  if (typeof window === "undefined") return false;
  grantSupportConsent();

  if (showIntercomMessages()) return true;
  await ensureIntercomConfigured();
  const booted = await bootIntercom(lastBootTraits);
  if (booted && showIntercomMessages()) {
    trackIntercomEvent("concierge_conversation_opened", {
      conversation_id_present: !!opts.conversationId,
      page: window.location.pathname,
    });
    return true;
  }
  return false;
}

export function buildMailtoFallback(opts: OpenSupportChatOptions): string {
  const base = opts.mailtoFallback;
  if (!opts.subject && !opts.prefill) return base;
  const params: string[] = [];
  if (opts.subject) params.push(`subject=${encodeURIComponent(opts.subject)}`);
  if (opts.prefill) params.push(`body=${encodeURIComponent(opts.prefill)}`);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${params.join("&")}`;
}
